import React, { useState } from 'react';
import { 
  Target, 
  ArrowRight, 
  Rocket, 
  Users, 
  CheckCircle, 
  BarChart3,
  Play,
  RefreshCw,
  Download
} from 'lucide-react';

interface M2Model {
  id: string;
  name: string;
  description: string;
  structure: {
    [key: string]: {
      label: string;
      description: string;
      icon: React.ComponentType<any>;
      color: string;
    };
  };
}

interface M1Template {
  id: string;
  name: string;
  basedOn: string; // M2 model ID
  fields: {
    [key: string]: {
      label: string;
      type: 'text' | 'textarea' | 'select';
      options?: string[];
      required: boolean;
    };
  };
}

interface M0Runtime {
  id: string;
  name: string;
  templateId: string;
  data: {
    [key: string]: string;
  };
  status: 'draft' | 'in-progress' | 'completed';
}

export const M2AsM1Template: React.FC = () => {
  const [selectedM2Model, setSelectedM2Model] = useState<string>('outcome-driver');
  const [selectedM1Template, setSelectedM1Template] = useState<string>('outcome-driver-template');
  const [m0Runtime, setM0Runtime] = useState<M0Runtime>({
    id: 'demo-runtime',
    name: 'Demo Outcome Driver',
    templateId: 'outcome-driver-template',
    data: {
      currentState: 'Peak Priorities has a robust philosophy and content pipeline...',
      problemOpportunity: 'The story exists in parts across docs and models...',
      impact: 'Without a concise visual story, momentum stalls...'
    },
    status: 'in-progress'
  });

  const m2Models: M2Model[] = [
    {
      id: 'outcome-driver',
      name: 'Outcome Driver',
      description: 'Current State → Problem/Opportunity → Impact',
      structure: {
        currentState: {
          label: 'Current State',
          description: 'Facts only - what is true today',
          icon: Target,
          color: 'amber'
        },
        problemOpportunity: {
          label: 'Problem/Opportunity',
          description: 'The gap, neutral tone',
          icon: ArrowRight,
          color: 'coral'
        },
        impact: {
          label: 'Impact',
          description: 'Why it matters',
          icon: Rocket,
          color: 'primary'
        }
      }
    },
    {
      id: 'desired-outcome',
      name: 'Desired Outcome Framework',
      description: 'Who → What → Evidence',
      structure: {
        who: {
          label: 'Who Wants It?',
          description: 'The stakeholder and their need',
          icon: Users,
          color: 'sage'
        },
        what: {
          label: 'What It Gives Them',
          description: 'The benefits and outcomes',
          icon: CheckCircle,
          color: 'primary'
        },
        evidence: {
          label: 'Evidence It\'s Done',
          description: 'How we know it\'s complete',
          icon: BarChart3,
          color: 'amber'
        }
      }
    }
  ];

  const m1Templates: M1Template[] = [
    {
      id: 'outcome-driver-template',
      name: 'Outcome Driver Template',
      basedOn: 'outcome-driver',
      fields: {
        currentState: {
          label: 'Current State',
          type: 'textarea',
          required: true
        },
        problemOpportunity: {
          label: 'Problem/Opportunity',
          type: 'textarea',
          required: true
        },
        impact: {
          label: 'Impact',
          type: 'textarea',
          required: true
        }
      }
    },
    {
      id: 'desired-outcome-template',
      name: 'Desired Outcome Template',
      basedOn: 'desired-outcome',
      fields: {
        who: {
          label: 'Who Wants It?',
          type: 'text',
          required: true
        },
        what: {
          label: 'What It Gives Them',
          type: 'textarea',
          required: true
        },
        evidence: {
          label: 'Evidence It\'s Done',
          type: 'textarea',
          required: true
        }
      }
    }
  ];

  const currentM2Model = m2Models.find(m => m.id === selectedM2Model);
  const currentM1Template = m1Templates.find(t => t.id === selectedM1Template);
  const currentM2ForTemplate = m2Models.find(m => m.id === currentM1Template?.basedOn);

  const handleRuntimeUpdate = (field: string, value: string) => {
    setM0Runtime(prev => ({
      ...prev,
      data: {
        ...prev.data,
        [field]: value
      }
    }));
  };

  const getColorClasses = (color: string) => {
    const colors: { [key: string]: any } = {
      amber: {
        bg: 'bg-amber-50',
        border: 'border-amber-200',
        text: 'text-amber-900',
        accent: 'text-amber-600',
        badge: 'bg-amber-100 text-amber-800'
      },
      coral: {
        bg: 'bg-coral-50',
        border: 'border-coral-200',
        text: 'text-coral-900',
        accent: 'text-coral-600',
        badge: 'bg-coral-100 text-coral-800'
      },
      primary: {
        bg: 'bg-primary-50',
        border: 'border-primary-200',
        text: 'text-primary-900',
        accent: 'text-primary-600',
        badge: 'bg-primary-100 text-primary-800'
      },
      sage: {
        bg: 'bg-sage-50',
        border: 'border-sage-200',
        text: 'text-sage-900',
        accent: 'text-sage-600',
        badge: 'bg-sage-100 text-sage-800'
      }
    };
    return colors[color] || colors.primary;
  };

  return (
    <div className="space-y-8">
      <div className="text-center">
        <h2 className="text-3xl font-heading font-bold text-charcoal-900 mb-4">
          M2 Model as M1 Template in M0 Runtime
        </h2>
        <p className="text-lg text-charcoal-700 font-body max-w-3xl mx-auto">
          See how our M2 models become M1 templates that power M0 runtime instances. 
          This demonstrates the four-layer architecture in action.
        </p>
      </div>

      <div className="grid lg:grid-cols-3 gap-6">
        {/* M2 Model Layer */}
        <div className="bg-gradient-to-br from-primary-50 to-sage-50 rounded-xl p-6 border-2 border-primary-200">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-8 h-8 bg-primary-100 rounded-full flex items-center justify-center">
              <Target className="h-4 w-4 text-primary-600" />
            </div>
            <h3 className="text-xl font-heading font-semibold text-charcoal-900">M2: Model</h3>
          </div>
          
          <div className="space-y-4">
            <select 
              value={selectedM2Model}
              onChange={(e) => setSelectedM2Model(e.target.value)}
              className="w-full p-2 border border-charcoal-200 rounded-lg font-body"
            >
              {m2Models.map(model => (
                <option key={model.id} value={model.id}>{model.name}</option>
              ))}
            </select>
            
            {currentM2Model && (
              <div className="space-y-3">
                <p className="text-sm text-charcoal-600 font-body">{currentM2Model.description}</p>
                <div className="bg-white border border-primary-200 rounded-lg p-3">
                  <h4 className="font-heading font-semibold text-charcoal-900 text-sm mb-1">What M2 Models Do:</h4>
                  <p className="text-xs text-charcoal-600 font-body">Define the structure, relationships, and rules. Think of this as the "blueprint" or "schema" that describes how something should work.</p>
                </div>
                {Object.entries(currentM2Model.structure).map(([key, structure]) => {
                  const Icon = structure.icon;
                  const colors = getColorClasses(structure.color);
                  return (
                    <div key={key} className={`${colors.bg} ${colors.border} border rounded-lg p-3`}>
                      <div className="flex items-center gap-2 mb-1">
                        <Icon className={`h-4 w-4 ${colors.accent}`} />
                        <span className={`font-heading font-semibold text-sm ${colors.text}`}>
                          {structure.label}
                        </span>
                      </div>
                      <p className="text-xs text-charcoal-600 font-body">{structure.description}</p>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>

        {/* M1 Template Layer */}
        <div className="bg-gradient-to-br from-coral-50 to-amber-50 rounded-xl p-6 border-2 border-coral-200">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-8 h-8 bg-coral-100 rounded-full flex items-center justify-center">
              <RefreshCw className="h-4 w-4 text-coral-600" />
            </div>
            <h3 className="text-xl font-heading font-semibold text-charcoal-900">M1: Template</h3>
          </div>
          
          <div className="space-y-4">
            <select 
              value={selectedM1Template}
              onChange={(e) => setSelectedM1Template(e.target.value)}
              className="w-full p-2 border border-charcoal-200 rounded-lg font-body"
            >
              {m1Templates.map(template => (
                <option key={template.id} value={template.id}>{template.name}</option>
              ))}
            </select>
            
            {currentM1Template && currentM2ForTemplate && (
              <div className="space-y-3">
                <p className="text-sm text-charcoal-600 font-body">
                  Based on: <span className="font-semibold">{currentM2ForTemplate.name}</span>
                </p>
                <div className="bg-white border border-coral-200 rounded-lg p-3">
                  <h4 className="font-heading font-semibold text-charcoal-900 text-sm mb-1">What M1 Templates Do:</h4>
                  <p className="text-xs text-charcoal-600 font-body">Convert the M2 model into a usable form with specific fields, types, and validation rules. Think of this as the "form" or "worksheet" that people actually fill out.</p>
                </div>
                {Object.entries(currentM1Template.fields).map(([key, field]) => (
                  <div key={key} className="bg-white border border-charcoal-200 rounded-lg p-3">
                    <label className="block font-heading font-semibold text-sm text-charcoal-900 mb-1">
                      {field.label}
                      {field.required && <span className="text-coral-600 ml-1">*</span>}
                    </label>
                    <div className="text-xs text-charcoal-500 font-body">
                      Type: {field.type}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* M0 Runtime Layer */}
        <div className="bg-gradient-to-br from-coral-50 to-amber-50 rounded-xl p-6 border-2 border-coral-200">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-8 h-8 bg-coral-100 rounded-full flex items-center justify-center">
              <Play className="h-4 w-4 text-coral-600" />
            </div>
            <h3 className="text-xl font-heading font-semibold text-charcoal-900">M0: Runtime</h3>
          </div>
          
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <span className="text-sm font-body text-charcoal-600">Status:</span>
              <span className={`px-2 py-1 rounded-full text-xs font-body ${
                m0Runtime.status === 'completed' ? 'bg-green-100 text-green-800' :
                m0Runtime.status === 'in-progress' ? 'bg-amber-100 text-amber-800' :
                'bg-gray-100 text-gray-800'
              }`}>
                {m0Runtime.status}
              </span>
            </div>
            
            {currentM1Template && (
              <div className="space-y-3">
                {Object.entries(currentM1Template.fields).map(([key, field]) => (
                  <div key={key} className="bg-white border border-charcoal-200 rounded-lg p-3">
                    <label className="block font-heading font-semibold text-sm text-charcoal-900 mb-2">
                      {field.label}
                    </label>
                    {field.type === 'textarea' ? (
                      <textarea
                        value={m0Runtime.data[key] || ''}
                        onChange={(e) => handleRuntimeUpdate(key, e.target.value)}
                        className="w-full p-2 border border-charcoal-200 rounded text-sm font-body resize-none"
                        rows={3}
                        placeholder={`Enter ${field.label.toLowerCase()}...`}
                      />
                    ) : (
                      <input
                        type="text"
                        value={m0Runtime.data[key] || ''}
                        onChange={(e) => handleRuntimeUpdate(key, e.target.value)}
                        className="w-full p-2 border border-charcoal-200 rounded text-sm font-body"
                        placeholder={`Enter ${field.label.toLowerCase()}...`}
                      />
                    )}
                  </div>
                ))}
                
                <div className="flex gap-2 pt-2">
                  <button className="flex-1 bg-primary-600 text-white px-3 py-2 rounded-lg text-sm font-body hover:bg-primary-700 transition-colors">
                    Save
                  </button>
                  <button className="flex-1 bg-charcoal-600 text-white px-3 py-2 rounded-lg text-sm font-body hover:bg-charcoal-700 transition-colors">
                    <Download className="h-4 w-4 inline mr-1" />
                    Export
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="bg-gradient-to-r from-sage-50 to-primary-50 rounded-xl p-6 border border-sage-200">
        <h4 className="text-lg font-heading font-semibold text-charcoal-900 mb-3">How This Works</h4>
        <div className="grid md:grid-cols-3 gap-4 text-sm font-body text-charcoal-700">
          <div className="flex items-start gap-3">
            <div className="w-6 h-6 bg-indigo-100 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
              <span className="text-indigo-600 font-bold text-xs">M2</span>
            </div>
            <div>
              <div className="font-semibold">Model Definition</div>
              <div>Defines the structure and relationships</div>
            </div>
          </div>
          <div className="flex items-start gap-3">
            <div className="w-6 h-6 bg-primary-100 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
              <span className="text-primary-600 font-bold text-xs">M1</span>
            </div>
            <div>
              <div className="font-semibold">Template Creation</div>
              <div>Converts model into usable template</div>
            </div>
          </div>
          <div className="flex items-start gap-3">
            <div className="w-6 h-6 bg-coral-100 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
              <span className="text-coral-600 font-bold text-xs">M0</span>
            </div>
            <div>
              <div className="font-semibold">Runtime Instance</div>
              <div>Live, editable instance of the template</div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
